package com.mgacreative.hotel.feature.reports.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.domain.repository.AgencyRepository
import com.mgacreative.hotel.domain.repository.ReservationRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

data class AgencyStats(
    val agencyName: String,
    val reservationCount: Int,
    val totalRevenue: Double
)

data class DemographicStats(
    val country: String,
    val reservationCount: Int,
    val percentage: Float
)

data class SalesStats(
    val month: String,
    val revenue: Double
)

data class StatisticsUiState(
    val isLoading: Boolean = false,
    val startDate: String = "",
    val endDate: String = "",
    val totalOccupancyRate: Float = 0f,
    val totalRevenue: Double = 0.0,
    val totalReservationsCount: Int = 0,
    val agencyStats: List<AgencyStats> = emptyList(),
    val demographicStats: List<DemographicStats> = emptyList(),
    val salesStats: List<SalesStats> = emptyList()
)

class StatisticsViewModel(
    private val reservationRepository: ReservationRepository,
    private val agencyRepository: AgencyRepository,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _uiState = MutableStateFlow(StatisticsUiState())
    val uiState: StateFlow<StatisticsUiState> = _uiState.asStateFlow()

    init {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val start = today.toString()
        val end = today.toString() // In a real app we might subtract/add days
        
        _uiState.value = _uiState.value.copy(
            startDate = start,
            endDate = end
        )
        fetchData(start, end)
    }

    fun updateDateRange(startDate: String, endDate: String) {
        _uiState.value = _uiState.value.copy(startDate = startDate, endDate = endDate)
        fetchData(startDate, endDate)
    }

    private fun fetchData(startDate: String, endDate: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(isLoading = true)

            try {
                // 1. Fetch Reservations and Agencies
                val allReservations = reservationRepository.getReservations()
                val agencies = agencyRepository.getAgencies()
                
                // Filter reservations within the date range
                // A simple string comparison works for YYYY-MM-DD
                val filteredReservations = allReservations.filter {
                    it.checkInDate >= startDate && it.checkInDate <= endDate
                }

                // 2. Compute General Stats
                val totalRev = filteredReservations.sumOf { it.totalPrice }
                val count = filteredReservations.size
                
                // For a real occupancy rate, we would need total rooms. 
                // We'll calculate a mock percentage based on max 50 rooms per day for this example.
                val days = 1 // Simplified: calculate exact days diff in real app
                val totalRoomsAvailable = 50 * days
                val occRate = if (totalRoomsAvailable > 0) (count.toFloat() / totalRoomsAvailable) * 100 else 0f

                // 3. Compute Agency Stats
                val agencyMap = agencies.associateBy { it.id }
                val agencyGrouped = filteredReservations.groupBy { it.agencyId }
                val aStats = agencyGrouped.mapNotNull { (agencyId, resList) ->
                    if (agencyId != null && agencyMap.containsKey(agencyId)) {
                        AgencyStats(
                            agencyName = agencyMap[agencyId]?.name ?: "Bilinmeyen Acenta",
                            reservationCount = resList.size,
                            totalRevenue = resList.sumOf { it.totalPrice }
                        )
                    } else null
                }

                // 4. Compute Demographic Stats
                val countryGrouped = filteredReservations.groupBy { it.guests?.country ?: "Bilinmiyor" }
                val dStats = countryGrouped.map { (country, resList) ->
                    val cCount = resList.size
                    val pct = if (count > 0) (cCount.toFloat() / count) * 100 else 0f
                    DemographicStats(
                        country = country,
                        reservationCount = cCount,
                        percentage = pct
                    )
                }.sortedByDescending { it.reservationCount }

                // 5. Compute Sales Stats
                // Group by month of check_in_date (YYYY-MM)
                val salesGrouped = filteredReservations.groupBy { it.checkInDate.substring(0, 7) }
                val sStats = salesGrouped.map { (month, resList) ->
                    SalesStats(
                        month = month,
                        revenue = resList.sumOf { it.totalPrice }
                    )
                }.sortedBy { it.month }

                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    totalRevenue = totalRev,
                    totalReservationsCount = count,
                    totalOccupancyRate = occRate,
                    agencyStats = aStats,
                    demographicStats = dStats,
                    salesStats = sStats
                )

            } catch (e: Exception) {
                e.printStackTrace()
                _uiState.value = _uiState.value.copy(isLoading = false)
            }
        }
    }
}
